/**
 * StageList.tsx — the mockup's progress list: ✓ complete, ● current, ○ pending.
 *
 * Shared by both transform modes and by both surfaces (the routed panel and the
 * chat flow), which previously each carried their own copy of this markup.
 */

import styles from "./StageList.module.css";

export interface StageListProps {
  readonly stages: readonly string[];
  /** Index of the running stage. Everything before it reads as complete. */
  readonly activeIndex: number;
  readonly title: string;
  /** Drop the card frame — for use inside a chat bubble, which supplies its own. */
  readonly bare?: boolean;
}

type StageState = "done" | "active" | "pending";

const MARKER: Record<StageState, string> = { done: "✓", active: "●", pending: "○" };

function stageState(index: number, activeIndex: number): StageState {
  if (index < activeIndex) return "done";
  return index === activeIndex ? "active" : "pending";
}

export function StageList({ stages, activeIndex, title, bare = false }: Readonly<StageListProps>) {
  const stateClass: Record<StageState, string> = {
    done: styles.stageDone!,
    active: styles.stageActive!,
    pending: styles.stagePending!,
  };

  return (
    <div className={`${styles.progress} ${bare ? styles.bare : ""}`}>
      <p className={styles.progressTitle}>
        <span className={styles.spinner} aria-hidden="true" />
        {title}
      </p>
      <ul className={styles.stageList}>
        {stages.map((stage, index) => {
          const state = stageState(index, activeIndex);
          return (
            <li key={stage} className={`${styles.stage} ${stateClass[state]}`}>
              <span aria-hidden="true">{MARKER[state]}</span> {stage}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
